import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  applyForceRetry,
  BulkEnvironmentMoveResult,
  buildEnvironmentMoveRequests,
  environmentLabel,
  Environment,
  forceableBlockedKeys,
  isForceableBlock,
  MAX_ENVIRONMENT_MOVE_KEYS,
  mergeEnvironmentMoveResults,
  selectionSourceEnvironment,
  summarizeEnvironmentMove,
} from '../utils/bulkEnvironmentMove';
import { authHeaders, bulkUpdateWalletShopProductEnvironment } from '../utils/walletShopApi';

export type MovableProduct = {
  productKey: string;
  productName: string;
  environment?: Environment;
  quantitySold: number;
  isActive: boolean;
};

type Props = {
  /** The selected cards (only those currently visible). */
  items: MovableProduct[];
  /** Called after any request that moved something: clear the selection and refetch. */
  onMoved: () => void;
  /** Another bulk action is running. */
  disabled?: boolean;
};

type PendingMove = { from: Environment; to: Environment; keys: string[]; force: boolean };
type LastMove = { from: Environment; to: Environment; result: BulkEnvironmentMoveResult };

const nameList = (names: string[], max = 4) =>
  names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} and ${names.length - max} more`;

/**
 * "Move to Prod / Move to Dev" for the selected products: in-page confirm (stronger for prod → dev),
 * then a result panel listing blocked items with the server's reason, and a "Move anyway" (Force)
 * retry for items blocked only because they sold in prod.
 */
const BulkEnvironmentMove: React.FC<Props> = ({ items, onMoved, disabled }) => {
  const [pending, setPending] = useState<PendingMove | null>(null);
  const [working, setWorking] = useState(false);
  const [last, setLast] = useState<LastMove | null>(null);
  // Names survive the refetch that clears the selection.
  const [names, setNames] = useState<Record<string, string>>({});

  const source = selectionSourceEnvironment(items);
  const displayName = (key: string) => names[key] || key;

  const run = async (move: PendingMove) => {
    const auth = authHeaders();
    if ('error' in auth) {
      toast.error(auth.error);
      return;
    }
    setWorking(true);
    try {
      const requests = buildEnvironmentMoveRequests(move.keys, move.from, move.to, move.force);
      // Sequential so a 400 on a later batch still leaves earlier batches' results visible.
      const results: BulkEnvironmentMoveResult[] = [];
      let failure = '';
      for (const request of requests) {
        try {
          results.push(await bulkUpdateWalletShopProductEnvironment(auth.headers, request));
        } catch (error) {
          failure = (error as Error).message || 'Failed to move products';
          break;
        }
      }
      if (!results.length) {
        toast.error(failure || 'Failed to move products');
        return;
      }
      const batchResult = mergeEnvironmentMoveResults(...results);
      const result = move.force && last ? applyForceRetry(last.result, move.keys, batchResult) : batchResult;
      setLast({ from: move.from, to: move.to, result });
      const summary = summarizeEnvironmentMove(move.force ? batchResult : result, move.to);
      const text = move.force ? `Move anyway: ${summary.text}` : summary.text;
      if (failure) toast.error(`${text} — then failed: ${failure}`);
      else if (summary.tone === 'success') toast.success(text);
      else toast.warn(text);
      onMoved();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to move products');
    } finally {
      setWorking(false);
      setPending(null);
    }
  };

  const openConfirm = () => {
    if ('error' in source) return;
    setNames((prev) => ({ ...prev, ...Object.fromEntries(items.map((i) => [i.productKey, i.productName || i.productKey])) }));
    setPending({ from: source.from, to: source.to, keys: items.map((i) => i.productKey), force: false });
  };

  const forceKeys = last ? forceableBlockedKeys(last.result.Blocked, last.from, last.to) : [];

  const renderConfirm = (move: PendingMove) => {
    const toDev = move.from === 'prod' && move.to === 'dev';
    const selected = items.filter((i) => move.keys.includes(i.productKey));
    const sold = selected.filter((i) => i.quantitySold > 0);
    const active = selected.filter((i) => i.isActive);
    const batches = Math.ceil(move.keys.length / MAX_ENVIRONMENT_MOVE_KEYS);
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4'>
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='bulk-env-move-title'
          className={`w-full max-w-lg rounded-2xl border p-6 space-y-4 bg-slate-900 ${toDev ? 'border-red-400/60' : 'border-white/15'}`}
        >
          <h2 id='bulk-env-move-title' className='text-lg font-semibold text-white'>
            {move.force
              ? `Move ${move.keys.length} sold product(s) to dev anyway?`
              : `Move ${move.keys.length} product(s) from ${move.from} to ${move.to}?`}
          </h2>
          {toDev ? (
            <div className='bg-red-500/10 border border-red-400/50 rounded-lg px-3 py-2 text-xs text-red-200 space-y-1'>
              <p className='font-semibold'>
                Warning: this removes {move.keys.length === 1 ? 'it' : 'them'} from the live shop. Real users will no longer see or buy{' '}
                {move.keys.length === 1 ? 'it' : 'them'}.
              </p>
              {move.force ? (
                <p>These products have sold in prod. Force overrides that block (it never overrides the active-bundle block).</p>
              ) : sold.length > 0 ? (
                <p>
                  {sold.length} of them {sold.length === 1 ? 'has' : 'have'} sales ({nameList(sold.map((i) => i.productName || i.productKey))}
                  ). The server blocks sold products unless you choose “Move anyway” afterwards.
                </p>
              ) : null}
              <p>Products used by an active premium bundle are always refused.</p>
            </div>
          ) : (
            <div className='bg-amber-500/10 border border-amber-400/40 rounded-lg px-3 py-2 text-xs text-amber-200 space-y-1'>
              <p>
                {active.length > 0
                  ? `${active.length} of them ${active.length === 1 ? 'is' : 'are'} active and will appear in the live shop for real users.`
                  : 'All of them are inactive; they will not be listed in the live shop until activated.'}
              </p>
              <p>Products used by an active premium bundle are refused.</p>
            </div>
          )}
          {batches > 1 && (
            <p className='text-xs text-gray-400'>
              Sent in {batches} batches of up to {MAX_ENVIRONMENT_MOVE_KEYS}.
            </p>
          )}
          <div className='flex justify-end gap-2'>
            <button
              type='button'
              onClick={() => setPending(null)}
              disabled={working}
              className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={() => run(move)}
              disabled={working}
              className={`px-4 py-2 text-xs font-semibold rounded-lg text-white disabled:opacity-50 ${
                toDev ? 'bg-red-500/90 hover:bg-red-500' : 'bg-emerald-500/90 hover:bg-emerald-500'
              }`}
            >
              {working
                ? 'Moving...'
                : move.force
                  ? `Move ${move.keys.length} anyway`
                  : `Confirm move to ${environmentLabel(move.to)}`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!items.length && !last) return null;

  return (
    <div className='mb-5 space-y-3'>
      {items.length > 0 && (
        <div className='flex flex-wrap items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3'>
          <span className='text-xs text-gray-300'>Environment</span>
          {'error' in source ? (
            <>
              <button
                type='button'
                disabled
                className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white opacity-50 cursor-not-allowed'
              >
                Move to Prod / Dev
              </button>
              <span className='text-xs text-amber-300'>{source.error}</span>
            </>
          ) : (
            <>
              <span className='text-xs text-gray-400'>
                {items.length} selected in {source.from}
              </span>
              <button
                type='button'
                onClick={openConfirm}
                disabled={disabled || working}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg text-white transition disabled:opacity-50 ${
                  source.to === 'dev' ? 'bg-red-500/80 hover:bg-red-500' : 'bg-emerald-500/80 hover:bg-emerald-500'
                }`}
              >
                {`Move to ${environmentLabel(source.to)} (${items.length})`}
              </button>
            </>
          )}
        </div>
      )}

      {last && (
        <div
          aria-label='Environment move result'
          className='bg-white/5 border border-white/10 rounded-xl px-4 py-3 space-y-2 text-xs'
        >
          <div className='flex items-center justify-between gap-3'>
            <span className='font-semibold text-white'>
              {last.from} → {last.to}: {summarizeEnvironmentMove(last.result, last.to).text}
            </span>
            <button type='button' onClick={() => setLast(null)} className='text-gray-400 hover:text-white'>
              Dismiss
            </button>
          </div>
          {last.result.Blocked.length > 0 && (
            <div className='space-y-1'>
              <div className='text-amber-300 font-semibold'>Blocked ({last.result.Blocked.length})</div>
              <ul className='space-y-0.5'>
                {last.result.Blocked.map((b) => (
                  <li key={b.ProductKey} className='text-gray-200'>
                    <span className='font-mono'>{b.ProductKey}</span>
                    {displayName(b.ProductKey) !== b.ProductKey ? ` (${displayName(b.ProductKey)})` : ''} — {b.Reason}
                    {last.from === 'prod' && last.to === 'dev' && isForceableBlock(b.Reason) ? (
                      <span className='ml-1 text-red-300'>· can be forced</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {last.result.NotFound.length > 0 && (
            <div className='text-gray-300'>
              <span className='text-gray-400 font-semibold'>Not found in {last.from}:</span>{' '}
              <span className='font-mono'>{last.result.NotFound.join(', ')}</span>
            </div>
          )}
          {forceKeys.length > 0 && (
            <button
              type='button'
              onClick={() => setPending({ from: 'prod', to: 'dev', keys: forceKeys, force: true })}
              disabled={working}
              className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white disabled:opacity-50'
            >
              {`Move anyway (${forceKeys.length} sold)`}
            </button>
          )}
        </div>
      )}

      {pending && renderConfirm(pending)}
    </div>
  );
};

export default BulkEnvironmentMove;
